import Image from "next/image";
import { Phone, MapPin } from "lucide-react";
import { BlockProps } from "../BlocksManager/registry";

export default function PresentationContact({ data }: BlockProps) {
  if (!data) return null;

  const imageUrl = data?.image?.url;

  return (
    <section className="overflow-x-hidden bg-white py-16 lg:py-20" key={data.id}>
      <div className="container overflow-hidden">
        <div className="grid items-center gap-10 lg:grid-cols-[420px_minmax(0,1fr)] lg:gap-12">
          {/* Partie gauche - Image circulaire avec badge */}
          <div className="relative flex justify-center lg:justify-start">
            {imageUrl && (
              <div className="relative w-full max-w-[380px]">
                {/* Cercle bleu décoratif en arrière-plan */}
                <div className="absolute left-0 top-0 h-[280px] w-[280px] rounded-full border-[8px] border-blue opacity-30 -z-10 sm:h-[340px] sm:w-[340px] lg:h-[400px] lg:w-[400px]" />

                {/* Forme bleue décorative à droite */}
                <div className="absolute right-0 bottom-8 h-[120px] w-[120px] rounded-full bg-blue opacity-40 -z-10 sm:h-[150px] sm:w-[150px] lg:right-[-40px] lg:bottom-10 lg:h-[200px] lg:w-[200px]" />

                {/* Image circulaire */}
                <div className="relative mx-auto h-[280px] w-[280px] overflow-hidden rounded-full border-[8px] border-blue/20 sm:h-[340px] sm:w-[340px] lg:h-[380px] lg:w-[380px]">
                  <Image
                    src={imageUrl}
                    alt={data.image?.alternativeText || data.nom_contact || "Contact"}
                    fill
                    className="object-cover"
                  />
                </div>

                {/* Badge nom et rôle */}
                {(data.nom_contact || data.role_contact) && (
                  <div className="absolute bottom-4 left-4 rounded-full bg-blue px-4 py-2 shadow-lg sm:bottom-6 sm:left-6 sm:px-5 sm:py-3 lg:bottom-8 lg:left-8 lg:px-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/30">
                        <div className="h-6 w-6 rounded-full bg-white" />
                      </div>
                      <div>
                        {data.nom_contact && (
                          <p className="text-[15px] font-semibold leading-tight text-white sm:text-[16px]">
                            {data.nom_contact}
                          </p>
                        )}
                        {data.role_contact && (
                          <p className="text-[12px] leading-tight text-white/90 sm:text-[13px]">
                            {data.role_contact}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Partie droite - Texte et informations */}
          <div className="min-w-0">
            {data.titre && (
              <h2 className="font-literata text-[32px] font-bold leading-[1.15] text-text-body lg:text-[52px]">
                {data.titre}
              </h2>
            )}

            {data.description && (
              <p className="mt-6 text-[17px] leading-[1.6] text-text-body lg:text-[19px]">
                {data.description}
              </p>
            )}

            {/* Informations de contact */}
            <div className="mt-10 space-y-5">
              {data.adresse && (
                <div className="flex items-center gap-4">
                  <div className="mt-1 flex-shrink-0">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue/10">
                      <MapPin className="h-5 w-5 text-blue" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[18px] font-semibold text-text-body break-words">
                      {data.adresse}
                    </p>
                  </div>
                </div>
              )}

              {data.telephone && (
                <div className="flex items-center gap-4">
                  <div className="mt-1 flex-shrink-0">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue/10">
                      <Phone className="h-5 w-5 text-blue" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <a
                      href={`tel:${data.telephone.replace(/\s/g, "")}`}
                      className="text-[18px] font-semibold text-text-body transition hover:text-blue break-words"
                    >
                      {data.telephone}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}